import { useEffect, useState } from "react";
import { api, imgUrl } from "../lib/api";
import { Film, Play } from "lucide-react";

function VideoRow({ video, index, total }) {
  const isReversed = index % 2 === 1;
  const accent = isReversed ? "#FCA311" : "#E63946";

  const isYoutube = video.source_type === "youtube";
  const srcUrl = video.source_type === "dropbox" ? video.dropbox_url
    : video.source_type === "file" ? imgUrl(video.file_path)
    : null;

  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center py-10 ${isReversed ? "md:[direction:rtl]" : ""}`}
      data-testid={`video-row-${index}`}
    >
      {/* Video card */}
      <div className="md:col-span-7 md:[direction:ltr]">
        <div
          className="relative bg-black aspect-video border-2 transition-colors duration-500 hover:shadow-[10px_10px_0_0_var(--accent)]"
          style={{ borderColor: accent, "--accent": accent }}
        >
          {isYoutube ? (
            <iframe
              title={video.title || `Video ${index + 1}`}
              src={`https://www.youtube.com/embed/${video.youtube_id}?rel=0&modestbranding=1&playsinline=1&cc_load_policy=0&cc_lang_pref=en&hl=en`}
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          ) : srcUrl ? (
            <video
              src={srcUrl}
              poster={video.poster_path ? imgUrl(video.poster_path) : undefined}
              controls
              playsInline
              className="absolute inset-0 w-full h-full object-cover bg-black"
            >
              <track kind="captions" />
            </video>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/30">
              <Play className="w-10 h-10" />
            </div>
          )}
          {/* Corner index badge */}
          <div className="absolute -top-3 left-4 px-2 py-0.5 font-mono text-[10px] tracking-[0.3em] bg-black" style={{ color: accent, border: `1px solid ${accent}` }}>
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
        </div>
      </div>

      {/* Text card */}
      <div className={`md:col-span-5 md:[direction:ltr] ${isReversed ? "md:text-right" : "md:text-left"}`}>
        <div className="font-mono text-[10px] tracking-[0.4em]" style={{ color: accent }}>
          // REEL {String(index + 1).padStart(2, "0")}
        </div>
        <h2 className="font-display text-4xl sm:text-5xl leading-[0.95] mt-2" data-testid={`video-row-title-${index}`}>
          {video.title || "(untitled)"}
        </h2>
        {video.description && (
          <p className="mt-4 text-white/65 text-base whitespace-pre-wrap leading-relaxed">{video.description}</p>
        )}
        <div className="mt-5 font-mono text-[10px] tracking-widest text-white/40">
          {video.source_type.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/feature-videos").then((r) => setVideos(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#0A0A0A] text-white" data-testid="videos-page">
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 py-16 lg:py-20">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-3">// THE REEL</div>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-none flex items-center gap-3 flex-wrap">
            <Film className="w-14 h-14 sm:w-20 sm:h-20 text-[#FCA311]" />
            VIDEOS.
          </h1>
          <p className="text-white/60 mt-4 max-w-xl">
            Music videos, behind-the-scenes, freestyles, full sessions.
          </p>
        </div>
      </section>

      {/* Reel */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 py-10">
            <div className="md:col-span-7 aspect-video bg-[#151515] animate-pulse" />
            <div className="md:col-span-5 space-y-3">
              <div className="h-3 w-24 bg-[#151515] animate-pulse" />
              <div className="h-10 w-3/4 bg-[#151515] animate-pulse" />
              <div className="h-4 w-2/3 bg-[#151515] animate-pulse" />
            </div>
          </div>
        ) : videos.length === 0 ? (
          <div className="font-display text-3xl py-20 text-center text-white/40" data-testid="videos-empty">
            NO VIDEOS YET. PULL UP LATER.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {videos.map((v, i) => (
              <VideoRow key={v.id} video={v} index={i} total={videos.length} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
